import React, { useState } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import Select from "react-select";
import "./AcedSelect.scss";

export type SelectOption = {
  value: string | null | boolean | undefined;
  label: string;
};

export type SelectOptions = SelectOption[];

type BaseSelectProps = {
  label?: string;
  options: SelectOptions;
  className?: string;
  errorMessage?: string;
  placeholder?: string;
  isDisabled?: boolean;
  defaultValue?: SelectOption;
  defaultIndex?: number;
  isSearchable?: boolean;
};

// RHF props: must have control and name
type RHFSelectProps = BaseSelectProps & {
  control: Control<any, any>;
  name: string;
  rules?: RegisterOptions;
  // These must NOT exist here to avoid conflict
  value?: never;
  onChange?: never;
  setState?: never;
};

// Controlled props: must have value and onChange
type ControlledSelectProps = BaseSelectProps & {
  value: string | null | boolean | undefined;
  onChange: (value: string | null | boolean) => void;
  // No control/name here
  control?: never;
  name?: never;
  rules?: never;
  setState?: never;
};

// Uncontrolled props: no control, no value/onChange
type UncontrolledSelectProps = BaseSelectProps & {
  control?: never;
  name?: never;
  value?: never;
  onChange?: never;
  rules?: never;
  setState?: never;
};

type AcedSelectProps =
  | RHFSelectProps
  | ControlledSelectProps
  | UncontrolledSelectProps;

const AcedSelect: React.FC<AcedSelectProps> = (props) => {
  // Detect if RHF by presence of control and name
  if ("control" in props && "name" in props && props.control && props.name) {
    return <AcedSelectRHF {...props} />;
  }

  // Detect if Controlled by presence of value and onChange
  else if ("value" in props && "onChange" in props && props.onChange) {
    return <AcedSelectControlled {...props} />;
  } else return <></>;
};

const AcedSelectRHF: React.FC<RHFSelectProps> = ({
  control,
  name,
  rules,
  label,
  options,
  placeholder,
  isDisabled,
  defaultValue,
  className,
  errorMessage,
  isSearchable,
}) => {
  const shouldBeSearchable = isSearchable ?? options.length > 10;

  return (
    <div className={`aced-select-container ${className || ""}`}>
      {label && (
        <div className="label-container">
          <label className="select-label">
            {label}
            {rules?.required && "*"}
          </label>
        </div>
      )}
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={defaultValue?.value ?? null}
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => (
          <>
            <Select
              isDisabled={isDisabled}
              onBlur={onBlur}
              ref={ref}
              value={options.find((opt) => opt.value === value) ?? null}
              onChange={(val) => onChange((val as SelectOption)?.value ?? null)}
              options={options}
              placeholder={placeholder}
              classNamePrefix="sc"
              className="select-custom"
              styles={customStyles(isDisabled)}
              menuPortalTarget={document.body}
              menuPosition="absolute"
              menuPlacement="auto"
              menuShouldScrollIntoView={false}
              isSearchable={shouldBeSearchable}
            />
            {error && <p className="error-label">{error.message}</p>}
            {!error && errorMessage && (
              <p className="error-label">{errorMessage}</p>
            )}
          </>
        )}
      />
    </div>
  );
};

const AcedSelectControlled: React.FC<ControlledSelectProps> = ({
  value,
  onChange,
  label,
  options,
  placeholder,
  isDisabled,
  className,
  errorMessage,
  isSearchable,
}) => {
  const shouldBeSearchable = isSearchable ?? options.length > 10;

  return (
    <div className={`aced-select-container ${className || ""}`}>
      {label && (
        <div className="label-container">
          <label className="select-label">{label}</label>
        </div>
      )}
      <Select
        isDisabled={isDisabled}
        value={options.find((opt) => opt.value === value) ?? null}
        onChange={(val) => onChange((val as SelectOption)?.value ?? null)}
        options={options}
        placeholder={placeholder}
        classNamePrefix="sc"
        className="select-custom"
        styles={customStyles(isDisabled)}
        menuPortalTarget={document.body}
        menuPosition="absolute"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        isSearchable={shouldBeSearchable}
      />
      {errorMessage && <p className="error-label">{errorMessage}</p>}
    </div>
  );
};

const AcedSelectUncontrolled: React.FC<UncontrolledSelectProps> = ({
  label,
  options,
  placeholder,
  isDisabled,
  className,
  errorMessage,
  defaultValue,
  isSearchable,
}) => {
  const shouldBeSearchable = isSearchable ?? options.length > 10;
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    defaultValue ?? null
  );

  return (
    <div className={`aced-select-container ${className || ""}`}>
      {label && <label className="select-label">{label}</label>}
      <Select
        isDisabled={isDisabled}
        value={selectedOption}
        onChange={(val) => setSelectedOption(val as SelectOption)}
        options={options}
        placeholder={placeholder}
        classNamePrefix="sc"
        className="select-custom"
        styles={customStyles(isDisabled)}
        menuPortalTarget={document.body}
        menuPosition="absolute"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        isSearchable={shouldBeSearchable}
      />
      {errorMessage && <p className="error-label">{errorMessage}</p>}
    </div>
  );
};

const customStyles = (isDisabled?: boolean) => ({
  control: (baseStyles: any, state: any) => ({
    ...baseStyles,
    width: "336px",
    padding: "8px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#ffc629" : "gray",
    backgroundColor: isDisabled ? "lightgray" : "white",
    color: isDisabled ? "darkgray" : "black",
    cursor: isDisabled ? "not-allowed" : "pointer",
    "&:hover": {
      borderColor: "#ffc629",
    },
  }),
  input: (baseStyles: any) => ({
    ...baseStyles,
    caretColor: "transparent",
  }),
  option: (baseStyles: any, state: any) => ({
    ...baseStyles,
    padding: "16px",
    color: state.isSelected ? "white" : "#151b2c",
    backgroundColor: state.isSelected ? "#3a4f50" : "white",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#3a4f50",
      color: "white",
    },
  }),
  menuList: (baseStyles: any) => ({
    ...baseStyles,
    maxHeight: "40vh",
    overflowY: "auto",
    border: "1px solid rgb(17, 18, 77)",
    borderRadius: "5px",
    WebkitOverflowScrolling: "touch",
    "::-webkit-scrollbar": {
      width: "8px",
    },
    "::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
    },
  }),
  menu: (baseStyles: any) => ({
    ...baseStyles,
    position: "absolute",
  }),
  menuPortal: (baseStyles: any) => ({
    ...baseStyles,
    zIndex: 9999,
  }),
});

export default AcedSelect;
